/**
 * Renderer — owns the WebGL2 context, the scene and its water fog, issues the one draw
 * call per frame, and is the module that tells the player *in the page* when their
 * browser cannot run the game instead of leaving them a black canvas.
 */
import * as THREE from 'three';
import { WATER } from '../data/tuning.ts';
import { QUALITY } from '../data/quality.ts';
import { TOKENS } from '../voxel/Palette.ts';

/** SPEC §1 targets 1440p. Past 2x DPR the extra pixels are invisible and cost pure fill rate. */
const DEFAULT_PIXEL_RATIO_CAP = QUALITY.pixelCap;

function cssHex(hex: number): string {
  return '#' + (hex >>> 0).toString(16).padStart(6, '0');
}

/**
 * A full-page failure card in the game's own palette. Deliberately built with inline
 * styles: it has to survive style.css never having loaded.
 */
function showFatal(heading: string, body: string, detail: string): HTMLElement {
  const root = document.createElement('div');
  root.setAttribute('data-shall-shark-fatal', '');
  root.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'gap:18px',
    'padding:10vw',
    'box-sizing:border-box',
    'text-align:center',
    `background:${cssHex(TOKENS.abyss)}`,
    `color:${cssHex(TOKENS.bone)}`,
    'font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
    'font-size:13px',
    'line-height:1.8',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = 'SHALL SHARK';
  title.style.cssText = `font-size:26px;letter-spacing:0.42em;color:${cssHex(TOKENS.caustic)}`;

  const rule = document.createElement('div');
  rule.style.cssText = `width:min(420px,70vw);height:1px;background:${cssHex(TOKENS.caustic)};opacity:0.35`;

  const head = document.createElement('div');
  head.textContent = heading;
  head.style.cssText = `letter-spacing:0.3em;color:${cssHex(TOKENS.alarm)}`;

  const copy = document.createElement('div');
  copy.textContent = body;
  copy.style.cssText = 'max-width:52ch;opacity:0.82;letter-spacing:0.06em';

  // Not uppercase / not letter-spaced: this line is a raw driver string and has to stay legible.
  const note = document.createElement('div');
  note.textContent = detail;
  note.style.cssText = 'max-width:60ch;opacity:0.5;font-size:11px;line-height:1.6';

  root.append(title, rule, head, copy, note);
  (document.body ?? document.documentElement).appendChild(root);
  return root;
}

/** Returns null when WebGL2 is usable, otherwise the reason it is not. */
function probeWebGL2(): string | null {
  if (typeof WebGL2RenderingContext === 'undefined') {
    return 'This browser does not expose WebGL2RenderingContext.';
  }
  // Probe on a throwaway canvas. Calling getContext on the real canvas would lock in
  // *these* attributes, and three's own getContext call would then silently come back
  // without antialiasing.
  const probe = document.createElement('canvas');
  let gl: WebGL2RenderingContext | null = null;
  try {
    gl = probe.getContext('webgl2');
  } catch (err) {
    return err instanceof Error ? err.message : String(err);
  }
  if (gl === null) {
    return 'A WebGL2 context could not be created. Hardware acceleration is most likely disabled in your browser settings, or the GPU is blocklisted.';
  }
  gl.getExtension('WEBGL_lose_context')?.loseContext();
  return null;
}

/** Split out of the constructor so `readonly renderer` gets a single unconditional assignment. */
function createRenderer(canvas: HTMLCanvasElement): THREE.WebGLRenderer {
  const blocked = probeWebGL2();
  if (blocked !== null) {
    showFatal(
      'WEBGL2 REQUIRED',
      'SHALL SHARK renders every shark, bot and water pass through a WebGL2 pipeline. There is no fallback path.',
      blocked,
    );
    throw new Error(`WebGL2 unavailable — ${blocked}`);
  }
  try {
    return new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
      alpha: false,
      stencil: false,
      depth: true,
      preserveDrawingBuffer: false,
    });
  } catch (err) {
    showFatal(
      'RENDERER FAILED TO START',
      'WebGL2 reported as available but the renderer could not be created.',
      err instanceof Error ? err.message : String(err),
    );
    throw err;
  }
}

export class Renderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly canvas: HTMLCanvasElement;

  private cssW = 1;
  private cssH = 1;
  private ratio = 1;
  private ratioCap = DEFAULT_PIXEL_RATIO_CAP;
  private fatal: HTMLElement | null = null;

  /** Mutated in place every frame — the debug overlay reads this 60x/s and must not allocate. */
  private readonly statsObj = {
    drawCalls: 0,
    triangles: 0,
    programs: 0,
    geometries: 0,
    textures: 0,
  };

  private readonly onContextLost = (): void => {
    if (this.fatal !== null) return;
    this.fatal = showFatal(
      'GPU CONTEXT LOST',
      'The graphics driver dropped the rendering context. This usually resolves itself; if the page stays dark, reload it.',
      'webglcontextlost',
    );
  };

  private readonly onContextRestored = (): void => {
    this.fatal?.remove();
    this.fatal = null;
  };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.renderer = createRenderer(canvas);

    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = WATER.exposure;
    this.renderer.shadowMap.enabled = false;
    this.renderer.autoClear = true;

    this.scene = new THREE.Scene();
    // Background and fog are the same colour on purpose: past the fog horizon there is
    // nothing to see, so geometry has to dissolve into the clear colour, not meet a seam.
    this.scene.background = new THREE.Color(WATER.fogColor);
    this.scene.fog = new THREE.FogExp2(WATER.fogColor, WATER.fogDensity);

    canvas.addEventListener('webglcontextlost', this.onContextLost, false);
    canvas.addEventListener('webglcontextrestored', this.onContextRestored, false);

    this.resize();
  }

  get width(): number {
    return this.cssW;
  }

  get height(): number {
    return this.cssH;
  }

  get pixelRatio(): number {
    return this.ratio;
  }

  get stats(): { drawCalls: number; triangles: number; programs: number; geometries: number; textures: number } {
    return this.statsObj;
  }

  setPixelRatioCap(cap: number): void {
    this.ratioCap = Math.min(Math.max(cap, 0.25), 4);
    this.resize();
  }

  /** Returns true if the drawing buffer size actually changed. */
  resize(): boolean {
    // clientWidth/Height, not innerWidth: the canvas need not be the whole viewport, and
    // this is the only size that survives CSS scaling of the container. The innerWidth
    // fallback covers a canvas that has not been laid out yet — never fall back to
    // canvas.width, which would feed the buffer size back into itself and grow every call.
    const w = Math.max(1, this.canvas.clientWidth || window.innerWidth || 1);
    const h = Math.max(1, this.canvas.clientHeight || window.innerHeight || 1);
    const ratio = Math.min(window.devicePixelRatio || 1, this.ratioCap);

    const bufW = Math.floor(w * ratio);
    const bufH = Math.floor(h * ratio);
    const bufferChanged = bufW !== this.canvas.width || bufH !== this.canvas.height;
    if (!bufferChanged && ratio === this.ratio && w === this.cssW && h === this.cssH) return false;

    this.cssW = w;
    this.cssH = h;
    this.ratio = ratio;
    this.renderer.setPixelRatio(ratio);
    // updateStyle = false: the CSS already sizes the canvas, and letting three write
    // inline width/height styles fights the stylesheet on every resize.
    this.renderer.setSize(w, h, false);
    return bufferChanged;
  }

  render(camera: THREE.Camera): void {
    this.renderer.render(this.scene, camera);
    // AFTER the render: three zeroes info.render at the top of every render() call, so
    // sampling before would report the previous frame's numbers (or all zeroes on frame 0).
    const info = this.renderer.info;
    const s = this.statsObj;
    s.drawCalls = info.render.calls;
    s.triangles = info.render.triangles;
    s.programs = info.programs !== null ? info.programs.length : 0;
    s.geometries = info.memory.geometries;
    s.textures = info.memory.textures;
  }

  dispose(): void {
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost, false);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored, false);
    this.fatal?.remove();
    this.fatal = null;
    this.scene.clear();
    this.scene.background = null;
    this.scene.fog = null;
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }
}
